import { DataRow } from '../types/components/data-rendering'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { SortingKeyType } from './sortAlgorithm'
import { isSameDay } from 'date-fns'
export type Field = {
  name: string
  label: {
    en_US: string
    fr_CA: string
    es_MX: string
  }
  type: SortingKeyType
  hideInFilters?: boolean
}
export type Fields = Field[]
export type OperatorKey =
  | 'equals'
  | 'notEqual'
  | 'greaterThan'
  | 'lessThan'
  | 'is'
  | 'isNot'
  | 'isEmpty'
  | 'onDate'
  | 'beforeDate'
  | 'afterDate'
  | 'onOrBefore'
  | 'onOrAfter'
  | 'isTrue'
  | 'isFalse'
  | 'isAnyOf'
  | 'isNotAnyOf'
// export type Operations = (arg1: string | number, arg2?: string | number) => boolean
// type NumberBooleanFunction = (arg1: number, arg2: number) => boolean
// type StringBooleanFunction = (arg1: string, arg2: string) => boolean
// type OneStringBooleanFunction = (arg: string) => boolean
export type Operator = {
  key: OperatorKey
  operatesOn: SortingKeyType
  //TODO: Add operator operations type
  operation: any
}
export type DateModifierKey =
  | 'exactDate'
  | 'today'
  | 'tomorrow'
  | 'yesterday'
  | 'oneWeekPast'
  | 'oneWeekFuture'
  | 'oneMonthPast'
  | 'oneMonthFuture'
export type DateModifier = {
  key: DateModifierKey
  input?: boolean
  // date: <T extends string>() => T
  date: any
}
export type DateModifiers = DateModifier[]
export type Condition = {
  field: Field | null
  operation: Operator | null
  options?: string[]
  optionsKey?: string
  dateModifier?: DateModifier | null
  value: string | number | null
}
export type Conditions = Condition[]
export type Operators = Operator[]
export interface UseConditionalsReturn {
  conditionalFilterInput: Ref<any[]>
  conditionStack: Ref<Conditions>
  addCondition: (condition: Condition) => void
  addBlankCondition: () => void
  removeCondition: (index: number) => void
  // fields: Fields
  getOperators: Operators
  getOperatorsByType: (fieldType: SortingKeyType) => Operators
  returnFilteredData: ComputedRef<any[]>
  returnOperationByName: (name: OperatorKey) => Operator
  getDateModifiers: () => DateModifiers
  getDateByDateModifier: (name: DateModifierKey) => string
  getDateModifierByName: (name: DateModifierKey) => DateModifier
  /**Add a sortTable fn */
  sortTableFn: Ref<((data: DataRow[]) => DataRow[]) | undefined>
}
const dateModifiers: DateModifiers = [
  {
    key: 'exactDate',
    input: true,
    date: () => null,
  },
  {
    key: 'today',
    date: () => {
      const today: Date = new Date()
      return today.toLocaleDateString()
    },
  },
  {
    key: 'tomorrow',
    date: () => {
      const today = new Date()
      const tomorrow = new Date()
      tomorrow.setDate(today.getDate() + 1)
      return tomorrow.toLocaleDateString()
    },
  },
  {
    key: 'yesterday',
    date: () => {
      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      return yesterday.toLocaleDateString()
    },
  },
  {
    key: 'oneWeekPast',
    date: () => {
      const today = new Date()
      const week = new Date()
      week.setDate(today.getDate() - 7)
      return week.toLocaleDateString()
    },
  },
  {
    key: 'oneWeekFuture',
    date: () => {
      const today = new Date()
      const week = new Date()
      week.setDate(today.getDate() + 7)
      return week.toLocaleDateString()
    },
  },
  {
    key: 'oneMonthPast',
    date: () => {
      const today = new Date()
      const month = new Date()
      month.setMonth(today.getMonth() - 1)
      //Move back a month if ends up in the same month
      if (today.getMonth() == month.getMonth()) {
        month.setDate(0)
      }
      return month.toLocaleDateString()
    },
  },
  {
    key: 'oneMonthFuture',
    date: () => {
      const today = new Date()
      const month = new Date()
      month.setMonth(today.getMonth() + 1)
      //Move back a day if moved too far in time
      //(eg. Mar 31 to May 1, then minus one day to Apr 30)
      if (month.getMonth() - today.getMonth() > 1) {
        month.setDate(month.getDate() - 1)
      }
      return month.toLocaleDateString()
    },
  },
]
export const getDateModifier = (key: DateModifierKey) => {
  return dateModifiers.find((modifier) => {
    return modifier.key == key
  })!
}

const operators: Operators = [
  /*
   * Boolean operators
   */
  {
    key: 'isTrue',
    operatesOn: 'boolean',
    operation: (value1: boolean): boolean => {
      return value1
    },
  },
  {
    key: 'isFalse',
    operatesOn: 'boolean',
    operation: (value1: boolean): boolean => {
      return !value1
    },
  },
  /*
   * Number operators
   */
  {
    key: 'equals',
    operatesOn: 'number',
    operation: (value1: number, value2: number | string | null): boolean => {
      if (value2 === null) {
        return true
      }
      if (typeof value2 == 'string') {
        return Number(value1) === parseFloat(value2)
      }
      return Number(value1) === value2
    },
  },
  {
    key: 'notEqual',
    operatesOn: 'number',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return Number(value1) !== value2
    },
  },
  {
    key: 'greaterThan',
    operatesOn: 'number',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return Number(value1) > value2!
    },
  },
  {
    key: 'lessThan',
    operatesOn: 'number',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return Number(value1) < value2!
    },
  },
  /*
   * Time operators
   */
  {
    key: 'equals',
    operatesOn: 'time',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return value1 === value2
    },
  },
  {
    key: 'notEqual',
    operatesOn: 'time',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return value1 !== value2
    },
  },
  {
    key: 'greaterThan',
    operatesOn: 'time',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return value1 > value2!
    },
  },
  {
    key: 'lessThan',
    operatesOn: 'time',
    operation: (value1: number, value2: number | null): boolean => {
      if (value2 === null) {
        return true
      }
      return value1 < value2!
    },
  },
  /*
   * String operators
   */
  {
    key: 'is',
    operatesOn: 'string',
    operation: (string1: string, string2: string | null): boolean => {
      if (string2 === null) {
        return true
      }
      return String(string1).toUpperCase().trim() === String(string2).toUpperCase().trim()
    },
  },
  {
    key: 'isNot',
    operatesOn: 'string',
    operation: (string1: string, string2: string | null): boolean => {
      if (string2 === null) {
        return true
      }
      return String(string1).toUpperCase().trim() !== string2.toUpperCase().trim()
    },
  },
  {
    key: 'isEmpty',
    operatesOn: 'string',
    operation: (string1: string): boolean => {
      return String(string1).trim() === ''
    },
  },
  /*
   * Date operators
   */
  {
    key: 'onDate',
    operatesOn: 'date',
    operation: (date1: string, date2: string | null): boolean => {
      if (date2 === null) {
        return true
      }
      //need to add timestamp, otherwise dates are off!
      date2 = date2 + ' 00:00:00'
      return isSameDay(new Date(date1), new Date(date2))
    },
  },
  //before date, not including
  {
    key: 'beforeDate',
    operatesOn: 'date',
    operation: (date1: string, date2: string | null): boolean => {
      if (date2 === null) {
        return true
      }
      return new Date(date1.trim()).valueOf() < new Date(date2!.trim()).valueOf()
    },
  },
  //after date, not including
  {
    key: 'afterDate',
    operatesOn: 'date',
    operation: (date1: string, date2: string | null): boolean => {
      if (date2 === null) {
        return true
      }
      return new Date(date1.trim()).valueOf() > new Date(date2!.trim()).valueOf()
    },
  },
  {
    key: 'onOrBefore',
    operatesOn: 'date',
    operation: (date1: string, date2: string | null): boolean => {
      if (date2 === null) {
        return true
      }
      const d1 = new Date(date1.trim())
      const d2 = new Date(`${date2} 00:00:00.000`.trim())
      d1.setHours(0)
      d1.setMinutes(0)
      d1.setMilliseconds(0)
      d2.setHours(0)
      d2.setMinutes(0)
      d2.setMilliseconds(0)
      return new Date( d1).valueOf() <= new Date(d2).valueOf()
    },
  },
  {
    key: 'onOrAfter',
    operatesOn: 'date',
    operation: (date1: string, date2: string | null): boolean => {
      if (date2 === null) {
        return true
      }
      const d1 = new Date(date1.trim())
      const d2 = new Date(`${date2} 00:00:00.000`.trim())
      d1.setHours(0)
      d1.setMinutes(0)
      d1.setMilliseconds(0)
      d2.setHours(0)
      d2.setMinutes(0)
      d2.setMilliseconds(0)
      return new Date(d1).valueOf() >= new Date(d2).valueOf()
    },
  },
  /*
   * Array Operators
   */
  {
    key: 'isAnyOf',
    operatesOn: 'array',
    operation: (options: string[], vals: string[] | null): boolean => {
      if (!vals) {
        return true
      }
      for (const val of vals) {
        if (options && options.includes(val)) return true
      }
      return false
    },
  },
  {
    key: 'isNotAnyOf',
    operatesOn: 'array',
    operation: (options: string[], vals: string[] | null): boolean => {
      if (!vals) {
        return true
      }
      for (const val of vals) {
        if (!options.includes(val)) return true
      }
      return false
    },
  },
]

export const getOperators = (key: OperatorKey) => {
  return operators.find((operator) => {
    return operator.key == key
  })!
}

export function useConditionals(): UseConditionalsReturn {
  // const fields = ref<Fields>([])
  const conditionalFilterInput = ref<any[]>([])
  /*
   * Conditions logic
   */
  const conditionStack = ref<Conditions>([])

  const sortTableFn = ref<(data: DataRow[]) => DataRow[]>()
  const addCondition = (condition: Condition): void => {
    conditionStack.value.push(condition)
  }
  const addBlankCondition = (): void => {
    const newCondition = { field: null, operation: null, value: null }
    // conditionStack.value.push(newCondition)
    if (conditionStack.value.length === 0) {
      conditionStack.value.push(newCondition)
    } else {
      if (
        !conditionStack.value.map((condition) => condition.field).includes(null) &&
        !conditionStack.value.map((condition) => condition.operation).includes(null) &&
        !conditionStack.value.map((condition) => condition.value).includes(null)
      ) {
        conditionStack.value.push(newCondition)
      }
    }
  }
  const removeCondition = (index: number): void => {
    conditionStack.value.splice(index, 1)
  }
  /*
   * Operater logic
   */
  const getOperatorsByType = (fieldType: SortingKeyType): Operators => {
    if (fieldType === null) {
      return []
    }
    return operators.filter((operator: Operator) => {
      return operator.operatesOn === fieldType
    })
  }
  const returnOperationByName = (name: OperatorKey): Operator => {
    return operators.find((operator) => operator.key == name)!
  }
  /*
   * Date Modifier logic
   */
  const getDateByDateModifier = (name: DateModifierKey): string => {
    return dateModifiers.find((modifier) => modifier.key == name)!.date
  }
  const getDateModifiers = (): DateModifiers => {
    return dateModifiers
  }
  const getDateModifierByName = (name: DateModifierKey): DateModifier => {
    return dateModifiers.find((modifier) => modifier.key == name)!
  }
  /*
   * Filter data logic
   */

  //returns only complete conditions
  const conditions = computed<Conditions>(() => {
    const completeConditions: Conditions = []
    for (const condition of conditionStack.value) {
      if (condition.field != null && condition.operation != null) {
        completeConditions.push(condition)
      }
    }
    return completeConditions
  })

  const returnFilteredData = computed<DataRow[]>(() => {
    if (!conditionalFilterInput.value) {
      // I'm not quite sure where it's happening, but sometimes this is set to `undefined`
      // In that case, just handle it as if the array were empty instead
      return []
    }
    const dataToFilter: DataRow[] = [...conditionalFilterInput.value]
    if (conditionStack.value.length < 1) return dataToFilter

    const out: DataRow[] = []

    conditionLoop: for (let index = 0; index < dataToFilter.length; index++) {
      const row = dataToFilter[index]
      for (const condition of conditions.value) {
        if (
          condition.operation != null &&
          !condition.operation.operation(
            condition.options && condition.optionsKey
              ? //@ts-expect-error in this rare instance it would
                row[condition.field!.name].map((val) => val[condition.optionsKey])
              : row[condition.field!.name],
            condition.value
          )
        ) {
          continue conditionLoop
        }
      }

      out.push({
        ...row,
        _index: out.length,
      })
    }
    return out
  })

  return {
    conditionalFilterInput,
    conditionStack,
    addCondition,
    addBlankCondition,
    removeCondition,
    // fields: fields.value,
    getOperators: operators,
    getOperatorsByType,
    returnOperationByName,
    returnFilteredData,
    getDateModifiers,
    getDateByDateModifier,
    getDateModifierByName,
    sortTableFn,
  }
}
